import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import styled from 'styled-components';
import { AuthProvider } from './context/AuthProvider';
import { LanguageProvider } from './context/LanguageContext';
import Header from './shared/Header';
import Footer from './shared/Footer';

// Pages
import Home from './pages/Home';
import Categories from './pages/Categories';
import Brands from './pages/Brands';
import Brand from './pages/Brand';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import OrderConfirmation from './pages/OrderConfirmation';
import PaymentSuccess from './pages/PaymentSuccess';
import Wishlist from './pages/Wishlist';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ResetPassword from './pages/ResetPassword';
import NotFound from './pages/NotFound';
import Profile from './pages/Profile';
import MyOrders from './pages/MyOrders';
import SellWithUs from './pages/SellWithUs';
import TermsAndConditions from './pages/TermsAndConditions';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Contact from './pages/Contact';
import FAQ from './pages/FAQ';

// Components
import ProtectedRoute from './components/ProtectedRoute';

// Admin
import AdminLayout from './admin/components/AdminLayout';
import AdminLogin from './admin/pages/AdminLogin';
import AdminDashboard from './admin/pages/AdminDashboard';
import AdminProducts from './admin/pages/AdminProducts';
import AdminOrders from './admin/pages/AdminOrders';
import AdminBankDetails from './admin/pages/AdminBankDetails';
import AdminPromotions from './admin/pages/AdminPromotions';
import AdminPayments from './admin/pages/AdminPayments';
import AdminStatus from './admin/pages/AdminStatus';
import AdminVoucherCodes from './admin/pages/AdminVoucherCodes';
import AdminBrandSettings from './admin/pages/AdminBrandSettings';
import AdminProtectedRoute from './admin/components/ProtectedRoute';

// Super Admin
import SuperAdminLayout from './superadmin/components/SuperAdminLayout';
import SuperAdminLogin from './superadmin/pages/SuperAdminLogin';
import SuperAdminDashboard from './superadmin/pages/SuperAdminDashboard';
import SuperAdminProducts from './superadmin/pages/SuperAdminProducts';
import SuperAdminOrders from './superadmin/pages/SuperAdminOrders';
import SuperAdminBankDetails from './superadmin/pages/SuperAdminBankDetails';
import SuperAdminPromotions from './superadmin/pages/SuperAdminPromotions';
import SuperAdminPayments from './superadmin/pages/SuperAdminPayments';
import SuperAdminStatus from './superadmin/pages/SuperAdminStatus';
import SuperAdminVoucherCodes from './superadmin/pages/SuperAdminVoucherCodes';
import SuperAdminProtectedRoute from './superadmin/components/SuperAdminProtectedRoute';
import SuperAdminUsers from './superadmin/pages/SuperAdminUsers';
import SuperAdminBrands from './superadmin/pages/SuperAdminBrands';
import SuperAdminBanners from './superadmin/pages/SuperAdminBanners';
import SuperAdminCategories from './superadmin/pages/SuperAdminCategories';
import SuperAdminSubcategories from './superadmin/pages/SuperAdminSubcategories';
import SuperAdminShippingCountries from './superadmin/pages/SuperAdminShippingCountries';
import SuperAdminShippingRates from './superadmin/pages/SuperAdminShippingRates';
import SuperAdminSettings from './superadmin/pages/SuperAdminSettings';

const AppWrapper = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f9fafb;
  margin: 0;
  padding: 0;
`;

const MainContent = styled.main`
  flex: 1;
  padding-top: 150px; /* Account for fixed header */
  @media (max-width: 768px) {
    padding-top: 120px;
  }
`;

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <Router>
        <Routes>
          {/* Public routes with Header and Footer */}
          <Route path="/" element={
            <AppWrapper>
              <Header />
              <MainContent>
                <Home />
              </MainContent>
              <Footer />
            </AppWrapper>
          } />
          <Route path="/login" element={
            <AppWrapper>
              <Header />
              <MainContent>
                <Login />
              </MainContent>
              <Footer />
            </AppWrapper>
          } />
          <Route path="/signup" element={
            <AppWrapper>
              <Header />
              <MainContent>
                <Signup />
              </MainContent>
              <Footer />
            </AppWrapper>
          } />
          <Route path="/reset-password" element={
            <AppWrapper>
              <Header />
              <MainContent>
                <ResetPassword />
              </MainContent>
              <Footer />
            </AppWrapper>
          } />
          <Route path="/categories" element={
            <AppWrapper>
              <Header />
              <MainContent>
                <Categories />
              </MainContent>
              <Footer />
            </AppWrapper>
          } />
          <Route path="/categories/browse" element={
            <AppWrapper>
              <Header />
              <MainContent>
                <Categories />
              </MainContent>
              <Footer />
            </AppWrapper>
          } />
                 <Route path="/category/:categorySlug/:subcategorySlug?" element={
            <AppWrapper>
              <Header />
              <MainContent>
                <Categories />
              </MainContent>
              <Footer />
            </AppWrapper>
          } />
          <Route path="/products" element={
            <AppWrapper>
              <Header />
              <MainContent>
                <Categories />
              </MainContent>
              <Footer />
            </AppWrapper>
          } />
          <Route path="/brands" element={
            <AppWrapper>
              <Header />
              <MainContent>
                <Brands />
              </MainContent>
              <Footer />
            </AppWrapper>
          } />
          <Route path="/brands/:brandSlug" element={
            <AppWrapper>
              <Header />
              <MainContent>
                <Brand />
              </MainContent>
              <Footer />
            </AppWrapper>
          } />
          <Route path="/:brandSlug" element={
            <AppWrapper>
              <Header />
              <MainContent>
                <Brand />
              </MainContent>
              <Footer />
            </AppWrapper>
          } />
          <Route path="/product/:id" element={
            <AppWrapper>
              <Header />
              <MainContent>
                <ProductDetail />
              </MainContent>
              <Footer />
            </AppWrapper>
          } />
          <Route path="/cart" element={
            <AppWrapper>
              <Header />
              <MainContent>
                <Cart />
              </MainContent>
              <Footer />
            </AppWrapper>
          } />
          <Route path="/checkout" element={
            <AppWrapper>
              <Header />
              <MainContent>
                <Checkout />
              </MainContent>
              <Footer />
            </AppWrapper>
          } />
          <Route path="/order-confirmation/:orderId" element={
            <AppWrapper>
              <Header />
              <MainContent>
                <OrderConfirmation />
              </MainContent>
              <Footer />
            </AppWrapper>
          } />
          <Route path="/payment-success" element={
            <AppWrapper>
              <Header />
              <MainContent>
                <PaymentSuccess />
              </MainContent>
              <Footer />
            </AppWrapper>
          } />
          <Route path="/wishlist" element={
            <AppWrapper>
              <Header />
              <MainContent>
                <Wishlist />
              </MainContent>
              <Footer />
            </AppWrapper>
          } />

          <Route path="/profile" element={
            <AppWrapper>
              <Header />
              <MainContent>
                <Profile />
              </MainContent>
              <Footer />
            </AppWrapper>
          } />
          <Route path="/sell-with-us" element={
            <ProtectedRoute>
              <AppWrapper>
                <Header />
                <MainContent>
                  <SellWithUs />
                </MainContent>
                <Footer />
              </AppWrapper>
            </ProtectedRoute>
          } />

          <Route path="/orders" element={
            <AppWrapper>
              <Header />
              <MainContent>
                <MyOrders />
              </MainContent>
              <Footer />
            </AppWrapper>
          } />

          <Route path="/terms-and-conditions" element={
            <AppWrapper>
              <Header />
              <MainContent>
                <TermsAndConditions />
              </MainContent>
              <Footer />
            </AppWrapper>
          } />

          <Route path="/privacy-policy" element={
            <AppWrapper>
              <Header />
              <MainContent>
                <PrivacyPolicy />
              </MainContent>
              <Footer />
            </AppWrapper>
          } />

          <Route path="/contact" element={
            <AppWrapper>
              <Header />
              <MainContent>
                <Contact />
              </MainContent>
              <Footer />
            </AppWrapper>
          } />

          <Route path="/faq" element={
            <AppWrapper>
              <Header />
              <MainContent>
                <FAQ />
              </MainContent>
              <Footer />
            </AppWrapper>
          } />

          {/* Admin routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={
            <AdminProtectedRoute>
              <AdminLayout />
            </AdminProtectedRoute>
          }>
            <Route index element={<AdminDashboard />} />
            <Route path="products" element={<AdminProducts />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="status" element={<AdminStatus />} />
            <Route path="bank-details" element={<AdminBankDetails />} />
            <Route path="promotions" element={<AdminPromotions />} />
            <Route path="voucher-codes" element={<AdminVoucherCodes />} />
            <Route path="payments" element={<AdminPayments />} />
            <Route path="brand-settings" element={<AdminBrandSettings />} />
          </Route>

          {/* Super Admin routes */}
          <Route path="/superadmin/login" element={<SuperAdminLogin />} />
          <Route path="/superadmin" element={
            <SuperAdminProtectedRoute>
              <SuperAdminLayout />
            </SuperAdminProtectedRoute>
          }>
            <Route index element={<SuperAdminDashboard />} />
            <Route path="products" element={<SuperAdminProducts />} />
            <Route path="orders" element={<SuperAdminOrders />} />
            <Route path="users" element={<SuperAdminUsers />} />
            <Route path="brands" element={<SuperAdminBrands />} />
            <Route path="banners" element={<SuperAdminBanners />} />
                  <Route path="categories" element={<SuperAdminCategories />} />
                  <Route path="subcategories" element={<SuperAdminSubcategories />} />
                  <Route path="shipping-countries" element={<SuperAdminShippingCountries />} />
                  <Route path="shipping-rates" element={<SuperAdminShippingRates />} />
                  <Route path="status" element={<SuperAdminStatus />} />
            <Route path="bank-details" element={<SuperAdminBankDetails />} />
            <Route path="promotions" element={<SuperAdminPromotions />} />
            <Route path="voucher-codes" element={<SuperAdminVoucherCodes />} />
            <Route path="payments" element={<SuperAdminPayments />} />
            <Route path="settings" element={<SuperAdminSettings />} />
          </Route>

          {/* 404 */}
          <Route path="*" element={
            <AppWrapper>
              <Header />
              <MainContent>
                <NotFound />
              </MainContent>
              <Footer />
            </AppWrapper>
          } />
        </Routes>
        </Router>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
